import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { CopyButton } from '../../../utils/copyToClipboard';

interface CompanyWalletRequest {
  id: string;
  walletAddress: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  company: {
    id: string;
    companyName: string;
    companyEmail: string;
  };
}

interface CompanyWalletRequestsTableProps {
  requests: CompanyWalletRequest[];
  onApprove: (walletId: string) => void;
  onReject: (walletId: string) => void;
}

const CompanyWalletRequestsTable: React.FC<CompanyWalletRequestsTableProps> = ({
  requests,
  onApprove,
  onReject
}) => {
  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No pending company wallet requests.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-600">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Company
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Wallet Address
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Requested
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-[#182233] divide-y divide-gray-600">
          {requests.map((request) => (
            <tr key={request.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-white">{request.company.companyName}</div>
                  <div className="text-sm text-gray-400">{request.company.companyEmail}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                <div className="flex items-center space-x-2">
                  <div className="max-w-xs truncate">
                    {request.walletAddress.slice(0, 8)}...{request.walletAddress.slice(-8)}
                  </div>
                  <CopyButton text={request.walletAddress} />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  request.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                  request.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {request.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                {new Date(request.createdAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                {request.status === 'PENDING' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onApprove(request.id)}
                      className="text-green-600 hover:text-green-900"
                      title="Approve Request"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onReject(request.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Reject Request"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {request.status === 'REJECTED' && request.rejectionReason && (
                  <span className="text-red-600 text-xs">
                    {request.rejectionReason}
                  </span>
                )}
                {request.status === 'APPROVED' && (
                  <span className="text-green-600 text-sm">
                    Approved
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyWalletRequestsTable;
